//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import angular, {
  IAngularEvent,
  IDocumentService,
  ILocationProvider,
  ILocationService,
  IRootScopeService,
  ITimeoutService,
} from 'angular';
import {
  IState,
  IStateProvider,
  IStateService,
  IUrlRouterProvider,
} from 'angular-ui-router';
import { INgRedux } from 'ng-redux';

import { ShareRoutes } from '@share/constants/shareConstants';
import Transition from '@utils/Transition';
import { createCountdownTimer } from '@utils/countdown';

import { crl8 } from 'app/curalate.globals';
import appModule from 'app/curalate.module';
import * as routingActions from 'app/initialization/routing/routingActions';
import {
  IInitialUrlTraits,
  IStateWithParams,
  RedirectTarget,
} from 'app/initialization/routing/routingModels';
import { INamedState } from 'app/initialization/routing/stateHelpers';
import logger from 'app/logger/logger';
import { sessionModeStore } from 'app/moderation-mode/sessionModeStore';
import { topLevelRoutingRules, webappStates } from 'app/top-level-states';

appModule.config([
  '$locationProvider',
  '$stateProvider',
  '$urlRouterProvider',
  function CuralateRouting(
    $locationProvider: ILocationProvider,
    $stateProvider: IStateProvider,
    $urlRouterProvider: IUrlRouterProvider,
  ) {
    $locationProvider.html5Mode({
      enabled: true,
      rewriteLinks: true,
    });

    // If no state matches the provided url, show the error state
    $urlRouterProvider.otherwise('/error');

    // Ignore trailing slashes
    $urlRouterProvider.rule(
      ($injector: unknown, $location: ILocationService) => {
        const path = $location.url();

        // check to see if the path has a trailing slash
        if ('/' === path[path.length - 1]) {
          return path.replace(/\/$/, '');
        }

        // check to see if the path has a trailing slash and params
        if (path.indexOf('/?') > 0) {
          return path.replace('/?', '?');
        }

        return false;
      },
    );

    // Routing redirects
    topLevelRoutingRules.forEach((ruleFn) => {
      ruleFn($urlRouterProvider);
    });

    // Register the top level states from `topLevelStates`
    webappStates.forEach((stateConfig) => {
      $stateProvider.state(
        stateConfig.name,
        angular.extend(
          {
            onEnter: [
              '$document',
              ($document: IDocumentService) => {
                if (stateConfig.data?.title) {
                  const title =
                    typeof stateConfig.data.title === 'function'
                      ? stateConfig.data.title()
                      : stateConfig.data.title;
                  ($document[0] as unknown as Document).title =
                    `Bazaarvoice Vibe | ${title}`;
                }
              },
            ],
          },
          stateConfig,
        ),
      );
    });
  },
]);

appModule.run([
  '$rootScope',
  '$state',
  '$location',
  '$ngRedux',
  '$timeout',
  function StateChangeHandler(
    $rootScope: IRootScopeService,
    $state: IStateService,
    $location: ILocationService,
    $ngRedux: INgRedux,
    $timeout: ITimeoutService,
  ) {
    // On initial pageload, redirect any clients that have limited dashboard
    // access e.g. Share V2 clients.
    crl8.userSessionPromise.then((session) => {
      const isBazaarvoiceSyndicationEnabled =
        !!session?.features.bazaarvoice_network_syndication_frontend;

      if (session) {
        // Re-resolve the mode now the session is settled so render-time
        // consumers reflect the authoritative value.
        sessionModeStore.hydrate();
      }

      if (session?.client.isShareV2Vendor) {
        const path = $location.path();

        let validPaths = [];

        if (isBazaarvoiceSyndicationEnabled) {
          validPaths = [
            // BV Connect clients can hit galleries, insights, and account settings.
            // Everything else should result in redirect to galleries.
            // They can also Oauth fb & ig accounts
            '/galleries',
            '/insights',
            '/account-settings',
            '/facebook/oauth',
            '/instagram/oauth',
            '/tiktok/oauth',
            '/tiktok-ads/oauth',
            '/twitter/oauth',
            '/pinterest/oauth',
          ];
        } else {
          validPaths = [
            // Legacy Connect clients can OAuth FB and IG accounts, and also access the account
            // settings page and the main Connect page
            '/facebook/oauth',
            '/instagram/oauth',
            '/tiktok/oauth',
            '/tiktok-ads/oauth',
            '/twitter/oauth',
            '/pinterest/oauth',
            '/settings/account',
            '/share2',
            '/connect',
          ];
        }

        if (
          path &&
          !validPaths.find((validPath) => path.startsWith(validPath))
        ) {
          const rerouteState = isBazaarvoiceSyndicationEnabled
            ? 'galleries'
            : ShareRoutes.MAIN;
          $timeout(() =>
            $state.go(rerouteState, $location.search(), {
              location: 'replace',
            }),
          );
        }
      }
    });

    // Record our initial page load time so that we can automatically reload the first
    // state transition after 60 minutes has passed.
    const hasExceededSixtyMinutes = createCountdownTimer(60, 'minutes');

    /**
     * Grab information about the current url, meant to be used to grab the initial url.
     */
    function getInitialUrlTraits(): IInitialUrlTraits {
      const queryParams = $location.search();
      const stateFromCognito =
        queryParams.code && queryParams.state && JSON.parse(queryParams.state);
      return {
        rawPathAndParams: location.pathname + location.search + location.hash,
        stateFromCognito: stateFromCognito || undefined,
      };
    }

    // Figure out a redux-compatible way to describe this state + params
    function formatStateWithParams(
      state: INamedState,
      params: Record<string, unknown>,
      initialUrlTraits?: IInitialUrlTraits,
    ): IStateWithParams {
      return {
        state: {
          name: state.name,
          href: $state.href(state, params),
        },
        params,
        initialUrlTraits,
      };
    }
    $rootScope.$on(
      '$stateChangeStart',
      (
        event: IAngularEvent,
        toState: INamedState,
        toParams: Record<string, unknown>,
        fromState: INamedState,
        fromParams: Record<string, unknown>,
      ) => {
        const isInitialTransition =
          fromState.name === '' && fromState.url === '^';
        $ngRedux.dispatch(
          routingActions.stateTransitionStart(
            // Store initial additional URL params to try to help the post-login-redirect path go more smoothly.
            formatStateWithParams(
              toState,
              toParams,
              isInitialTransition ? getInitialUrlTraits() : undefined,
            ),
            formatStateWithParams(fromState, fromParams),
          ),
        );
        // Shim: Simulating ui-router 1.0 redirectTo behavior.
        const redirect = toState.redirectTo;
        if (redirect) {
          const redirectRes: RedirectTarget =
            typeof redirect === 'function'
              ? redirect(
                  new Transition(toState, toParams, fromState, fromParams),
                )
              : redirect;
          if (redirectRes) {
            event.preventDefault();
            // Let redux know we redirectin' so we can store details about where we redirected away from
            $ngRedux.dispatch(routingActions.stateTransitionRedirected());

            // Always replace, since the current route would not really be valid.
            if (typeof redirectRes === 'string') {
              logger.console.info(
                `Redirecting: ${toState.name} -> ${redirectRes}`,
              );
              $state.go(redirectRes, toParams, { location: 'replace' });
            } else {
              const { params, state } = redirectRes;
              logger.console.info(`Redirecting: ${toState.name} -> ${state}`);
              $state.go(
                state,
                { ...toParams, ...params },
                { location: 'replace' },
              );
            }
          }
        }

        // This state change warrants a full page refresh if more than
        // sixty minutes has passed between refreshes and the navigation
        // is triggered from a topNav link
        if (
          !event.defaultPrevented &&
          hasExceededSixtyMinutes() &&
          toParams.viaTopNav
        ) {
          window.location.href = $state.href(toState, toParams);
        }
        // Reset this state parameter to false, so it doesn't persist between navigations
        toParams.viaTopNav = false;
      },
    );

    $rootScope.$on(
      '$stateChangeSuccess',
      (
        event: IAngularEvent,
        toState: INamedState,
        toParams: Record<string, unknown>,
        fromState: INamedState,
        fromParams: Record<string, unknown>,
      ) => {
        $ngRedux.dispatch(
          routingActions.stateTransitionSuccess(
            formatStateWithParams(toState, toParams),
            formatStateWithParams(fromState, fromParams),
          ),
        );

        // If a scrollTo param was included in the route, set it as the URL's hash value
        if (typeof toParams.scrollTo === 'string') {
          $location.hash(toParams.scrollTo);
        }
      },
    );

    $rootScope.$on(
      '$stateChangeError',
      (
        event: IAngularEvent,
        toState: IState,
        toParams: Record<string, unknown>,
        fromState: IState,
        fromParams: Record<string, unknown>,
        error: unknown,
      ) => {
        $ngRedux.dispatch(routingActions.stateTransitionError(error));
      },
    );

    // !!!!!
    // DEBUGGING STATE CHANGES:
    // Uncomment this section to debug state changes.
    // !!!!!

    // $rootScope.$on('$stateChangeStart', (event, toState, toParams, fromState, fromParams) => {
    //   logger.console.log(
    //     `$stateChangeStart to ${toState.name}- fired when the transition begins. toState,toParams : \n`,
    //     toState,
    //     toParams,
    //   );
    // });

    // $rootScope.$on('$stateChangeError', (event, toState, toParams, fromState, fromParams, error) => {
    //   logger.console.log('$stateChangeError - fired when an error occurs during transition.');
    //   logger.console.log(error);
    // });

    // $rootScope.$on('$stateChangeSuccess', (event, toState, toParams, fromState, fromParams) => {
    //   logger.console.log(
    //     `$stateChangeSuccess to ${toState.name}- fired once the state transition is complete.`,
    //   );
    // });

    // $rootScope.$on('$viewContentLoaded', (event) => {
    //   logger.console.log('$viewContentLoaded - fired after dom rendered', event);
    // });

    // $rootScope.$on('$stateNotFound', (event, unfoundState, fromState, fromParams) => {
    //   logger.console.log(
    //     `$stateNotFound ${unfoundState.to} - fired when a state cannot be found by its name.`
    //   );
    //   logger.console.log(unfoundState, fromState, fromParams);
    // });
  },
]);
